import {
  isLive,
  makeEnvelope,
  parseEnvelope,
  RecordType,
  resolveSettings,
  systemHlc,
  tombstoneOf,
  type AnyEnvelope,
  type DataOf,
  type Envelope,
  type HlcClock,
  type Live,
  type LiveProject,
  type Settings,
} from '@wiztime/core';
import { NotFoundError, ValidationError } from '../errors';
import type { RepoWriter } from '../repo';
import type { ChangeEntry } from '../undo/types';

export interface SessionEnvironment {
  readonly clock: HlcClock;
  readonly deviceId: string;
  /** Current time in epoch milliseconds, read once when the command starts. */
  readonly nowMs: number;
  readonly tz: string;
  /** Creates identifiers of new records. */
  readonly newId: () => string;
}

/**
 * The scope of one user command: all of its reads and writes go through one repository
 * transaction. Every `write` stamps the record with a fresh hlc and this device, validates it,
 * and remembers the before and after images so the command becomes one undo step.
 */
export class WriteSession {
  private readonly changes = new Map<string, { before: AnyEnvelope | null; after: AnyEnvelope }>();
  private cachedSettings: Settings | undefined;

  constructor(
    readonly tx: RepoWriter,
    private readonly env: SessionEnvironment,
  ) {}

  get nowMs(): number {
    return this.env.nowMs;
  }

  get tz(): string {
    return this.env.tz;
  }

  get deviceId(): string {
    return this.env.deviceId;
  }

  newId(): string {
    return this.env.newId();
  }

  /** The resolved settings as of the start of the command (re-read after a setting is written). */
  async settings(): Promise<Settings> {
    this.cachedSettings ??= resolveSettings(await this.tx.listByType(RecordType.Setting));
    return this.cachedSettings;
  }

  /** The record or null when it does not exist (a tombstone is returned as is). */
  async find(id: string): Promise<AnyEnvelope | null> {
    return (await this.tx.get(id)) ?? null;
  }

  /** The live record `id` of `type`, or a NotFoundError. */
  async require<T extends RecordType>(type: T, id: string): Promise<Live<Envelope<T>>> {
    const record = await this.tx.get(id);
    if (record === undefined || record.type !== type || !isLive(record)) {
      throw new NotFoundError(id, type);
    }
    return record as Live<Envelope<T>>;
  }

  async requireProject(id: string): Promise<LiveProject> {
    return this.require(RecordType.Project, id);
  }

  /**
   * Write `data` (null deletes) as record `id`; this is part of the command's undo step. The
   * record gets a timestamp newer than the stored one, even when another tab or a remote merge
   * wrote the stored one with a clock that runs ahead of this store's.
   */
  async write<T extends RecordType>(type: T, id: string, data: DataOf<T> | null): Promise<Envelope<T>> {
    const earlier = this.changes.get(id);
    const stored = earlier === undefined ? ((await this.tx.get(id)) ?? null) : earlier.after;
    const before = earlier === undefined ? stored : earlier.before;
    if (stored !== null) this.env.clock.receive(stored.hlc);
    const envelope = makeEnvelope({
      id,
      type,
      data,
      hlc: this.env.clock.tick(),
      deviceId: this.env.deviceId,
    });
    const parsed = parseEnvelope(envelope);
    if (!parsed.success) throw new ValidationError(`Invalid ${type} record`, parsed.issues);
    await this.tx.put(parsed.data);
    this.changes.set(id, { before, after: parsed.data });
    if (type === RecordType.Setting) this.cachedSettings = undefined;
    return parsed.data as Envelope<T>;
  }

  /**
   * Delete an expired draft as a system action: it is not part of the undo step, and the
   * tombstone carries the system timestamp, which any user write on any device beats. A
   * confirmed draft on another device therefore survives the purge when the devices sync.
   * User deletions go through `write` with `null` and a real timestamp.
   */
  async purge(record: AnyEnvelope): Promise<void> {
    await this.tx.put(tombstoneOf(record, systemHlc(this.env.deviceId), this.env.deviceId));
  }

  /** The before and after images of the writes so far. */
  entries(): ChangeEntry[] {
    return [...this.changes.entries()].map(([id, change]) => ({
      id,
      before: change.before,
      after: change.after,
    }));
  }
}
